import React from "react";
import {
  WSActions,
  WSCard,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import { WSQueries } from "../../../../query/WSQuery";
import { useQueryMeridioPlanCatalog } from "../../../../query/benefits/useQueryMeridioPlanCatalog";
import { useFlowContext } from "../../../../shared/components/Flow";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { Currency } from "@wingspanhq/utils/dist/currency/currencies";
import {
  IMeridioPlanCatalogCategory,
  IMeridioPlanCatalogResponse,
  IMeridioPlanOption
} from "../../../../services/api/benefits/meridioPlanCatalog/types";
import { CoverageTier, EnrollMeridioFlowState } from "./types";
import { FlowStep } from "../../../../shared/components/FlowSwitch/types";

const TIER_LABELS: Record<CoverageTier, string> = {
  IO: "Individual (just you)",
  IS: "Individual + Spouse (you and your spouse)",
  IC: "Individual + Dependents (you and your child(ren))",
  IF: "Family (you, spouse, and children)"
};

const TIER_KEY_MAP: Record<CoverageTier, "io" | "is" | "ic" | "if"> = {
  IO: "io",
  IS: "is",
  IC: "ic",
  IF: "if"
};

function findPlan(
  categories: IMeridioPlanCatalogCategory[],
  baseSku: string
): IMeridioPlanOption | undefined {
  for (const cat of categories) {
    const found = (cat.plans as IMeridioPlanOption[]).find(
      p => p.baseSku === baseSku
    );
    if (found) return found;
  }
  return undefined;
}

export const StepConfirmPlans: React.FC<FlowStep> = props => {
  const queryMeridioPlanCatalog = useQueryMeridioPlanCatalog();
  const [flowState, setFlowState] = useFlowContext<EnrollMeridioFlowState>();

  const handleContinue = () => {
    setFlowState(prev => ({ ...prev, plansConfirmed: true }));
    props.onNext?.();
  };

  const allSelectedSkus = [
    flowState.healthPlanSku,
    ...(flowState.voluntaryPlanSkus ?? []),
    flowState.dentalVisionPlanSku
  ].filter((s): s is string => !!s);

  return (
    <WSQueries queries={{ queryMeridioPlanCatalog }}>
      {({
        queryMeridioPlanCatalogData
      }: {
        queryMeridioPlanCatalogData: IMeridioPlanCatalogResponse;
      }) => (
        <>
          <WSText.ParagraphSm weight="book" color="gray500" mb="XL">
            Confirm your plan and coverage selections.
          </WSText.ParagraphSm>
          {allSelectedSkus.map(baseSku => {
            const plan = findPlan(
              queryMeridioPlanCatalogData.categories,
              baseSku
            );
            if (!plan) return null;
            const tier = flowState.planSelections?.[baseSku];
            const tierKey = tier ? TIER_KEY_MAP[tier] : undefined;
            const pricing = tierKey ? plan[tierKey] : undefined;
            return (
              <WSCard key={baseSku} mb="M">
                <WSText.Heading5 weight="medium" mb="M">
                  {plan.name}
                </WSText.Heading5>
                <WSList gap="S">
                  <WSText.ParagraphSm weight="book" color="gray500">
                    Who's covered
                  </WSText.ParagraphSm>
                  <WSText.ParagraphSm weight="book" color="gray700">
                    {tier ? TIER_LABELS[tier] : "—"}
                  </WSText.ParagraphSm>
                  <WSText.ParagraphSm mt="S" weight="book" color="gray500">
                    Cost
                  </WSText.ParagraphSm>
                  <WSText.ParagraphSm weight="book" color="gray700">
                    {pricing
                      ? `${formatMoney(pricing.monthlyPremium, Currency.USD)} per month`
                      : "—"}
                  </WSText.ParagraphSm>
                </WSList>
              </WSCard>
            );
          })}
          <WSActions
            mt="XL"
            alignment="fill"
            buttons={[
              { kind: "Tertiary", label: "Back", onClick: props.onBack },
              { label: "Continue", onClick: handleContinue }
            ]}
          />
        </>
      )}
    </WSQueries>
  );
};
